import { createInstance } from 'i18next'
import type { i18n, Module, Newable, NewableModule, TFunction } from 'i18next'
import { nbspPostProcessor } from './postprocessor'
import { resources } from './resources'
import type { AppLanguage } from './resources'

export interface CreateI18nOptions {
  /** Język startowy; domyślnie 'pl'. */
  lng?: AppLanguage
  /**
   * Dodatkowe pluginy i18next rejestrowane przez `use(...)` przed `init` —
   * warstwa web przekazuje tu `initReactI18next` (react-i18next nie jest
   * zależnością tego pakietu).
   */
  modules?: ReadonlyArray<Module | NewableModule<Module> | Newable<Module>>
}

export interface CreateI18nResult {
  /** Skonfigurowana, odizolowana instancja i18next (`createInstance`). */
  i18n: i18n
  /**
   * Promise z `init()` — zasoby są inline, więc rozwiązuje się od razu;
   * `await ready` przed pierwszym użyciem `t()` daje gwarancję gotowości.
   */
  ready: Promise<TFunction>
}

/**
 * Tworzy instancję i18next dla k1p4 (PLAN-IMPLEMENTACJI §8.4):
 *  - zasoby pl/en, namespace domyślny "common",
 *  - `fallbackLng: 'pl'` — polski jest językiem źródłowym produktu,
 *  - postProcessor "nbsp" (twarde spacje U+00A0 tylko dla pl),
 *  - `interpolation.escapeValue: false` — escapowaniem zajmuje się React.
 *
 * Używa `createInstance()`, więc równoległe instancje (testy, SSR) nie dzielą
 * stanu globalnego singletona i18next.
 */
export function createI18n(options: CreateI18nOptions = {}): CreateI18nResult {
  const { lng = 'pl', modules = [] } = options
  const instance = createInstance()
  instance.use(nbspPostProcessor)
  for (const mod of modules) {
    instance.use(mod)
  }
  const ready = instance.init({
    lng,
    fallbackLng: 'pl',
    resources,
    defaultNS: 'common',
    ns: ['common'],
    interpolation: { escapeValue: false },
    postProcess: ['nbsp'],
  })
  /*
   * Formater `{{x, number}}` (fala 9, grupa 92): liczby i surowe napisy
   * liczbowe z API („0.50") dostają zapis języka — przecinek i twardą spację
   * po polsku. Napis już sformatowany („1,5", „1/2") przechodzi bez zmian,
   * zamiast stać się „NaN" — szablon nie wie, kto i jak przygotował wartość.
   */
  instance.services.formatter?.add('number', (value: unknown, language: string | undefined, formatOptions: Intl.NumberFormatOptions) => {
    if (typeof value === 'string' && !RAW_NUMBER_RE.test(value.trim())) return value
    const number = typeof value === 'number' ? value : Number(value)
    if (!Number.isFinite(number)) return String(value)
    return new Intl.NumberFormat(language?.startsWith('en') ? 'en-GB' : 'pl-PL', {
      maximumFractionDigits: 2,
      ...formatOptions,
    }).format(number)
  })
  return { i18n: instance, ready }
}

/** Liczba zapisana tak, jak przychodzi z API: „8", „-0.50", „+2.7". */
const RAW_NUMBER_RE = /^[-+]?\d+(?:\.\d+)?$/
